<template>
  <Renderer
    v-if="isEnabled"
    :value="value"
    :schema="schema"
    :config="config"
    :parent-index="parentIndex"
    :data-key="schema.key"
    :data-field="schema.key"
    :default-expanded="defaultExpanded"
    v-on="{
      ...$listeners,
      input: $ev => $emit('input', $ev),
    }"
  />
</template>

<script>
import { MediaComponent } from '@kvass/media'
import { FileComponent } from '@kvass/file'
import FieldSlot from './Fields/Slot'
import Renderer from './Renderer'
import Fields from './Fields'
import { path } from 'ramda'
import fetch from './fetch'
import {
  RemoveBrackets,
  ValidateCondition,
  IsDisabled,
  IsDefined,
  CurlyTemplateReplacer,
  isDynamicComponent,
} from './utils'

import { setup as MediaSetup } from '@kvass/media'
import { setup as FileSetup } from '@kvass/file'
import { VueComponentUpload as upload } from '@kvass/storage'
import { getMediaTypes } from '@kvass/media-types'

import MediaOnDrop from './components/kvass-media/onDrop'
import FileOnDrop from './components/kvass-file/onDrop'

function loadMediaSetup(vue) {
  MediaSetup({
    upload,
    dropMessage: MediaOnDrop,
    labels: {
      image: vue.$tc('image', 1),
      select: vue.$t('select'),
      cancel: vue.$t('cancel'),
      save: vue.$t('save'),
      descriptionPlaceholder: vue.$t('mediaDescriptionPlaceholder'),
      selectMessage: vue.$t('mediaTypeDefaultMessage'),
    },
  })
}
function loadFileSetup(vue) {
  FileSetup({ upload, dropMessage: FileOnDrop })
}

function getComponent(value, validator, components = [], data, parentSlots, ctx) {
  let disabled = IsDisabled(value.disabled, data)

  const getDisabledFieldReason = () => {
    const key = [
      ctx.injection.disabledFieldsPrefix,
      ctx.schema.parentKey,
      ctx.schema.parentIndex,
      ctx.schema.key,
    ]
      .filter(v => ![undefined, null].includes(v))
      .join('.')

    const disabledFields = ctx.injection.disabledFields || {}
    const target = Object.entries(disabledFields).find(([field]) => {
      return field === key || `${key}.`.startsWith(field)
    })

    if (!target) return
    return target[1]
  }
  let disabledFieldsReason = getDisabledFieldReason()

  const fullKey = [value.parentKey, value.parentIndex, value.key]
    .filter(v => ![null, undefined].includes(v))
    .join('.')

  // if (component.includes(':'))
  if (isDynamicComponent(value.component))
    return {
      component: Fields.Dynamic,
      subcomponents: value.component,
      binding: {
        ...(path(['options', 'props', '_parent'], value) || {}),
        disabled,
      },
      parentKey: fullKey,
    }

  let component = RemoveBrackets(value.component)

  let defaultBinding = {
    ...(path(['options', 'props'], value) || {}),
    parentIndex: value.parentIndex,
    'schema-data': data,
    disabled: value.disabled
      ? disabled
      : IsDisabled(path(['options', 'props', 'disabled'], value), data) ||
        Boolean(disabledFieldsReason),
  }

  if (typeof path(['options', 'validation'], value) === 'string')
    defaultBinding.required = Boolean(
      (path(['options', 'validation'], value) || '').match('required'),
    )

  if (validator && fullKey in validator.fields) {
    defaultBinding['isValid'] = validator.fields[fullKey].valid
    defaultBinding['validationMessage'] = validator.fields[fullKey].error
  }

  let customComponent = components.find(c => c.name === component)

  if (customComponent) return { component: customComponent, binding: { ...defaultBinding } }

  switch (component) {
    default:
      return { component: Fields.Input, binding: { ...defaultBinding } }
    case 'Slot':
      const slotKey = fullKey.split('.').pop()
      if (!parentSlots[slotKey]) return {}

      return {
        component: FieldSlot,
        binding: {
          element: parentSlots[slotKey],
          ...defaultBinding,
        },
      }

    case 'CombineSelector':
      return { component: Fields.CombineSelector, binding: { ...defaultBinding } }
    case 'SelectModal':
    case 'Select':
      return { component: Fields.SelectModal, binding: { ...defaultBinding } }
    case 'SelectList':
      return { component: Fields.Select, binding: { ...defaultBinding } }
    case 'Date':
      return { component: Fields.Input, binding: { ...defaultBinding, type: 'date' } }
    case 'TextArea':
      return { component: Fields.TextArea, binding: { ...defaultBinding } }
    case 'Number':
      return {
        component: Fields.Number,
        binding: {
          ...defaultBinding,
          mask: {
            mask: Number,
            unmask: true,
            thousandsSeparator: ' ',
          },
        },
      }
    case 'Choice':
      return {
        component: Fields.Choice,
        binding: {
          ...defaultBinding,
        },
      }
    case 'Currency':
      return {
        component: Fields.Number,
        binding: {
          suffix: 'NOK',
          ...defaultBinding,
          align: 'right',
          mask: {
            mask: Number,
            unmask: true,
            thousandsSeparator: ' ',
          },
        },
      }
    case 'URL':
      return {
        component: Fields.Input,
        binding: {
          prefix: 'URL',
          ...defaultBinding,
        },
      }
    case 'MediaMultiple':
      return {
        component: MediaComponent,
        binding: {
          types: getMediaTypes(),
          uploadOptions: { compression: 'default', placement: 'inside', size: 'contain' },
          ...defaultBinding,
          multiple: true,
        },
      }
    case 'Media':
      return {
        component: MediaComponent,
        binding: {
          types: getMediaTypes(),
          uploadOptions: { compression: 'default', placement: 'inside', size: 'contain' },
          ...defaultBinding,
        },
      }
    case 'Image':
      return {
        component: MediaComponent,
        binding: {
          uploadOptions: { compression: 'default', placement: 'inside', size: 'contain' },
          ...defaultBinding,
        },
      }
    case 'Images':
      return {
        component: MediaComponent,
        binding: {
          uploadOptions: { compression: 'default' },
          ...defaultBinding,
          multiple: true,
        },
      }
    case 'ColorSelector':
      return {
        component: Fields.ColorSelector,
        binding: { ...defaultBinding },
      }
    case 'Instagram':
      return {
        component: Fields.Instagram,
        binding: { ...defaultBinding },
      }
    case 'FooterContent':
      return {
        component: Fields.FooterContent,
        binding: { ...defaultBinding },
      }
    case 'ResidentialTemplate':
      return {
        component: Fields.ResidentialTemplate,
        binding: { ...defaultBinding },
      }
    case 'File':
      return { component: FileComponent, binding: { ...defaultBinding } }
    case 'Files':
      return { component: FileComponent, binding: { ...defaultBinding, multiple: true } }
    case 'RichText':
      return { component: Fields.RichText, binding: { ...defaultBinding } }
    case 'Checkbox':
      return { component: Fields.Checkbox, binding: { ...defaultBinding } }
    case 'Checklist':
      return { component: Fields.Checklist, binding: { ...defaultBinding } }
    case 'Radio':
      return { component: Fields.Radio, binding: { ...defaultBinding } }
    case 'Alert':
      return { component: Fields.Alert, binding: { ...defaultBinding } }
    case 'Html':
      return { component: Fields.Html, binding: { ...defaultBinding } }
    case 'Label':
      return { component: Fields.Label, binding: { ...defaultBinding } }
    case 'Calc':
      return { component: Fields.Calc, binding: { ...defaultBinding } }
    case 'Tags':
      return { component: Fields.Tags, binding: { ...defaultBinding } }
    case 'Webcomponent':
      return { component: Fields.Webcomponent, binding: { ...defaultBinding, data } }
    case 'LocationSelector':
      return { component: Fields.LocationSelector, binding: { ...defaultBinding, data } }
    case 'PrismEditor':
      return { component: Fields.PrismEditor, binding: { ...defaultBinding } }
  }
}

export default {
  watch: {
    fetchUrl: {
      handler: 'fetchData',
      immediate: true,
    },
  },
  props: {
    value: null,
    schema: Object,
    defaultExpanded: Array,
    parentIndex: Number,
  },
  inject: ['injection'],
  computed: {
    parentSlots() {
      return this.injection.parentSlots
    },
    validator() {
      return this.injection.validator
    },
    components() {
      return this.injection.components
    },
    data() {
      return this.injection.data
    },
    disabledFields() {
      return this.injection.disabledFields || {}
    },
    isEnabled() {
      return ValidateCondition(path(['options', 'condition'], this.schema), this.schema, this.data)
    },
    fetchUrl() {
      const item = path(['options', 'fetch'], this.schema)
      if (!item) return

      const { url } = item

      return CurlyTemplateReplacer(url, this.data)
    },
    config() {
      let config = getComponent(
        this.schema,
        this.validator,
        this.components,
        this.data,
        this.parentSlots,
        this,
      )

      return {
        settings: this.schema.settings
          ? getComponent(
              this.schema,
              this.validator,
              this.components,
              this.data,
              this.parentSlots,
              this,
            )
          : null,
        placement: this.schema.placement,
        isList: this.schema.isList,
        removable: IsDefined(path(['options', 'removable'], this.schema), true),
        collapsible: IsDefined(path(['options', 'collapsible'], this.schema), false),
        expanded: IsDefined(path(['options', 'expanded'], this.schema), false),
        minWidth: IsDefined(path(['options', 'minWidth'], this.schema), 'initial'),
        addable: IsDefined(path(['options', 'addable'], this.schema), true),
        showLabelCount: IsDefined(path(['options', 'showLabelCount'], this.schema), false),
        componentName: isDynamicComponent(this.schema.component)
          ? 'Dynamic'
          : RemoveBrackets(this.schema.component),
        ...config,
      }
    },
  },
  methods: {
    fetchData() {
      if (!this.fetchUrl) return (this.promise = Promise.resolve())

      const { method = 'GET', data = '' } = path(['options', 'fetch'], this.schema)

      fetch(this.fetchUrl, {
        method,
        body: JSON.stringify(path(data.split('.'), this.data)),
      })
        .then(res => res.json())
        .then(res => this.$emit('input', res))
    },
  },
  created() {
    loadMediaSetup(this)
    loadFileSetup(this)
  },
  components: {
    Renderer,
    FieldSlot,
  },
}
</script>
